import { useEffect, useState } from 'react';
import { ArrowUp } from 'lucide-react';

/**
 * Floating "Back to top" button. Appears after 400 px of scroll. Sky→emerald
 * progress ring reflects how far the user has scrolled through the page.
 * Respects prefers-reduced-motion by using an instant jump instead of smooth.
 */
export default function ScrollToTopButton() {
  const [visible, setVisible] = useState(false);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const onScroll = () => {
      const y = window.scrollY || window.pageYOffset;
      const h = document.documentElement.scrollHeight - window.innerHeight;
      setVisible(y > 400);
      setProgress(h > 0 ? Math.min(1, y / h) : 0);
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const goTop = () => {
    const prefersReduced = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    window.scrollTo({ top: 0, behavior: prefersReduced ? 'auto' : 'smooth' });
  };

  const size = 52;
  const stroke = 3;
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const dashOffset = c * (1 - progress);

  return (
    <button
      type="button"
      onClick={goTop}
      aria-label="Retour en haut de page"
      data-testid="scroll-to-top-btn"
      className={
        'fixed z-40 bottom-6 right-6 sm:bottom-8 sm:right-8 flex items-center justify-center ' +
        'transition-all duration-300 ease-out group focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-400 rounded-full ' +
        (visible
          ? 'opacity-100 translate-y-0 pointer-events-auto'
          : 'opacity-0 translate-y-4 pointer-events-none')
      }
      style={{ width: size, height: size }}
    >
      {/* Progress ring */}
      <svg
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
        className="absolute inset-0 -rotate-90 pointer-events-none"
        aria-hidden="true"
      >
        <defs>
          <linearGradient id="scrollTopGradient" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#0284c7" />
            <stop offset="100%" stopColor="#10b981" />
          </linearGradient>
        </defs>
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          stroke="rgba(2, 132, 199, 0.15)"
          strokeWidth={stroke}
          fill="white"
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          stroke="url(#scrollTopGradient)"
          strokeWidth={stroke}
          fill="none"
          strokeDasharray={c}
          strokeDashoffset={dashOffset}
          strokeLinecap="round"
          style={{ transition: 'stroke-dashoffset 120ms linear' }}
        />
      </svg>

      {/* Inner disc + arrow */}
      <span
        className="relative flex items-center justify-center rounded-full shadow-lg shadow-sky-500/25 transition-transform duration-200 group-hover:-translate-y-0.5 group-active:translate-y-0"
        style={{
          width: size - 12,
          height: size - 12,
          background:
            'linear-gradient(135deg, #ffffff 0%, #f0f9ff 60%, #ecfdf5 100%)',
        }}
      >
        <ArrowUp
          className="w-5 h-5 text-sky-700 group-hover:text-emerald-600 transition-colors"
          strokeWidth={2.75}
        />
      </span>
    </button>
  );
}
